import { useCallback, useEffect, useMemo, useState } from "react";
import { AddOrUpdateTemplateProps } from "../../imports/add-or-update-template/AddOrUpdateTemplate";
import { ImportTemplate, ImportTemplateField } from "@/types";
import { mapEntityTypeToFields } from "@/entities/EntityHelper";
import {
  saveImportTemplate,
  getImportTemplateById,
  getImportTemplates,
} from "@/api/ImportTemplate";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import { ClientEntityTypeKeys } from "@/entities/client";
import { FundEntityTypeKeys } from "@/entities/fund/FundEntityType";

interface UseAddOrUpdateTemplateController {
  new: boolean;
  entityTypeNames: string[];
  template: ImportTemplate;
  tags: string[];
  addTag: () => void;
  removeTag: (index: number) => void;
  updateTag: (index: number, tag: string) => void;
  updateName: (name: string) => void;
  updateDescription: (description: string) => void;
  updateEntityType: (entityType: string) => void;
  updateField: (field: ImportTemplateField) => void;
  saveTemplate: () => Promise<void>;
  columns: string[];
  isDraggingOver: boolean;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: () => void;
  handleDrop: (e: React.DragEvent, field: any) => void;
  usedColumns: string[];
  setColumns: (columns: string[]) => void;
  isSaving: boolean;
}

export const useAddOrUpdateTemplateController = (
  props: AddOrUpdateTemplateProps
): UseAddOrUpdateTemplateController => {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [columns, setColumns] = useState<string[]>([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState<ImportTemplate>({
    name: "",
    description: "",
    entityType: "",
    tags: "",
    createdAt: moment().toISOString(),
    fields: [],
  } as unknown as ImportTemplate);

  const entityTypeNames = useMemo(() => {
    return [...ClientEntityTypeKeys, ...FundEntityTypeKeys];
  }, [props.context]);

  const usedColumns = currentTemplate.fields
    .map((field) => field.fromField)
    .filter(Boolean);

  const addTag = () => {
    setCurrentTemplate((prevState) => {
      return {
        ...prevState,
        tags: prevState.tags ? `${prevState.tags},` : " ",
      } as unknown as ImportTemplate;
    });
  };

  const updateTag = (index: number, value: string) => {
    setCurrentTemplate((prevState) => {
      const newState = { ...prevState };
      const tags = newState.tags.split(",");
      tags[index] = value;
      newState.tags = tags.join(",");
      return newState as unknown as ImportTemplate;
    });
  };

  const removeTag = (index: number) => {
    setCurrentTemplate((prevState) => {
      const newState = { ...prevState };
      const tags = newState.tags.split(",");
      tags.splice(index, 1);
      newState.tags = tags.join(",");
      return newState as unknown as ImportTemplate;
    });
  };

  const updateName = (name: string) => {
    setCurrentTemplate((prevState) => {
      return { ...prevState, name } as unknown as ImportTemplate;
    });
  };

  const updateDescription = (description: string) => {
    setCurrentTemplate((prevState) => {
      return { ...prevState, description } as unknown as ImportTemplate;
    });
  };

  const updateEntityType = (entityType: string) => {
    const fields = mapEntityTypeToFields(entityType);
    setCurrentTemplate((prevState) => {
      return { ...prevState, entityType, fields } as unknown as ImportTemplate;
    });
  };

  const updateField = (field: ImportTemplateField) => {
    setCurrentTemplate((prevState) => {
      const newState = { ...prevState };
      const index = newState.fields.findIndex(
        (f) => f.toField === field.toField
      );
      if (index !== -1) {
        newState.fields[index] = field;
      }
      return newState as unknown as ImportTemplate;
    });
  };

  const saveTemplate = useCallback(async () => {
    setIsSaving(true);
    try {
      // First save the template
      const saveResult = await saveImportTemplate({
        ...currentTemplate,
      });

      if (saveResult) {
        console.log("Template saved successfully");

        // After saving, fetch the template ID if this is a new template
        if (props.templateId === undefined) {
          // Attempt to find the template ID by name to select it after switching tabs
          const templates = await getImportTemplates();
          const savedTemplate = templates.find(
            (t) => t.name === currentTemplate.name
          );

          if (savedTemplate?.importTemplateId) {
            // Dispatch event to select the template in the parent component
            window.dispatchEvent(
              new CustomEvent("selectTemplate", {
                detail: { templateId: savedTemplate.importTemplateId },
              })
            );
          }
        }

        // Switch to existing tab
        window.dispatchEvent(
          new CustomEvent("switchTab", {
            detail: { tab: "existing" },
          })
        );
      }
    } catch (error) {
      console.error("Failed to save template:", error);
    } finally {
      setIsSaving(false);
    }
  }, [currentTemplate, props.templateId]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = () => {
    setIsDraggingOver(false);
  };

  const handleDrop = (e: React.DragEvent, field: any) => {
    e.preventDefault();
    setIsDraggingOver(false);
    const column = e.dataTransfer.getData("text/plain");
    updateField({
      ...field,
      fromField: column,
    });
  };

  useEffect(() => {
    //NEed to replace this with a call to the API to get the individual template
    const loadTemplate = async () => {
      if (props.templateId) {
        const template = await getImportTemplateById(props.templateId);

        if (template) {
          setCurrentTemplate(template);
        }
      } else {
        setCurrentTemplate({
          name: "",
          description: "",
          entityType: "",
          tags: "",
          createdAt: moment().toISOString(),
          fields: [],
        } as ImportTemplate);
      }
    };

    loadTemplate();
  }, [props.templateId]);

  return {
    new: props.templateId === undefined,
    entityTypeNames,
    template: currentTemplate,
    tags:
      currentTemplate.tags && currentTemplate.tags !== ""
        ? currentTemplate.tags.split(",")
        : [],
    addTag,
    removeTag,
    updateTag,
    updateName,
    updateDescription,
    updateEntityType,
    updateField,
    saveTemplate,
    columns,
    isDraggingOver,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    usedColumns,
    setColumns,
    isSaving,
  };
};
